import { type Fingering } from '../guitar/library.js'

export enum Duration {
  QuarterNote = 'q',
  HalfNote = 'h',
  WholeNote = 'w',
  EightsNote = '8',
  SixteenthNote = '16',
}

export enum NoteFinish {
  Vibrato = 'v',
  StrongVibrato = 'V',
}

/** https://en.wikipedia.org/wiki/ASCII_tab */
export interface TabStep {
  fingering: Fingering | Fingering[]

  duration?: Duration
  // articulation?: Articulation
  finish?: NoteFinish
}

export interface Tab {
  name?: string
  /** Tablang string */
  lang?: string
  steps: TabStep[]
}

const stringLabels = ['e', 'B', 'G', 'D', 'A', 'E']

export const tabToAscii = (tab: Tab) => {
  /** Map out the tab in a sequence. The sequence is sequence[string][sequenceNo] */
  /** @todo handle instruments with != 6 strings */
  const sequence: (number | '-')[][] = [[], [], [], [], [], []]
  const tabSteps = tab.steps

  for (let stepIndex = 0; stepIndex < tabSteps.length; stepIndex++) {
    const tabStep = tabSteps[stepIndex]
    const stringNums: number[] = [0, 1, 2, 3, 4, 5]
    const isLastStep = stepIndex === tabSteps.length - 1

    const fingerings = Array.isArray(tabStep.fingering) ? tabStep.fingering : [tabStep.fingering]

    if (fingerings.length > 0) {
      // Handle regular fingering
      /** @todo when one of the is double digit, we need to use '--' for the muted strings, or 7- for the single digit strings */
      const maxFret = Math.max(...fingerings.map(f => f.fret as number))
      const hasDoubleDigit = maxFret >= 10

      for (const str of stringNums) {
        const fingering = fingerings.find(f => f.str === str + 1)
        const step = fingering?.fret ? fingering.fret : '-'
        sequence[str].push(step)
        if (hasDoubleDigit && step.toString().length === 1) {
          // If the step is double digit, we need to add an extra '-' to this single string
          sequence[str].push('-')
        }
      }

      // Add trailing dash after each note step, except for the last step
      if (!isLastStep) {
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    } else {
      // Handle pause steps
      if (stepIndex === 0) {
        // First step is a pause - add two dashes (no previous trailing dash)
        for (const str of stringNums) {
          sequence[str].push('-')
          sequence[str].push('-')
        }
      } else {
        // Subsequent pause steps - add one dash
        // (the trailing dash from previous note steps provides the spacing)
        for (const str of stringNums) {
          sequence[str].push('-')
        }
      }
    }
  }

  // Check if this tab contains any pause steps (empty fingering arrays)
  const hasPauses = tab.steps.some(step => {
    const fingerings = Array.isArray(step.fingering) ? step.fingering : [step.fingering]
    return fingerings.length === 0
  })

  // Use different formats based on whether we have pauses
  if (hasPauses) {
    // For tabs with pauses, use format compatible with ASCII parser: |content|
    return sequence.map((s, idx) => `${stringLabels[idx]} |${s.join('')}|`)
  } else {
    // For tabs without pauses, use original format: |-content-|
    return sequence.map((s, idx) => `${stringLabels[idx]} |-${s.join('')}-|`)
  }
}
